import { cn } from "@/lib/utils";
import {
    Calendar as CalendarIcon,
    CaretLeft,
    CaretRight,
    Check,
    Clock,
    House,
    Smiley,
    TreePalm,
    X,
    Lightning
} from "@phosphor-icons/react";
import {
    addDays,
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    isToday,
    isWeekend,
    startOfMonth,
    startOfWeek,
    subMonths,
} from "date-fns";
import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

interface AttendanceData {
  date: Date;
  checkInTime?: string;
  checkOutTime?: string;
  status:
    | "present"
    | "absent"
    | "late"
    | "wfh"
    | "leave"
    | "sick"
    | "holiday"
    | "pending";
  totalHours?: number;
  notes?: string;
  is_auto_checked_out?: boolean;
  is_auto_checked_in?: boolean;
}

interface AttendanceCalendarProps {
  attendanceData: Record<string, AttendanceData>;
  onDateSelect?: (date: Date) => void;
  onCheckIn?: (date: Date) => void;
  onCheckOut?: (date: Date) => void;
}

const STATUS_CONFIG = {
  present: { color: "bg-green-500", label: "✓ Present", icon: Check },
  absent: { color: "bg-red-500", label: "✗ Absent", icon: X },
  late: { color: "bg-orange-500", label: "⏱ Late", icon: Clock },
  wfh: { color: "bg-blue-500", label: "🏠 WFH", icon: House },
  leave: { color: "bg-purple-500", label: "🏖 Leave", icon: TreePalm },
  sick: { color: "bg-pink-500", label: "🤒 Sick", icon: Smiley },
  holiday: { color: "bg-gray-500", label: "📅 Holiday", icon: CalendarIcon },
  pending: { color: "bg-amber-500", label: "⏳ Pending", icon: Clock },
};

export const AttendanceCalendar: React.FC<AttendanceCalendarProps> = ({
  attendanceData,
  onDateSelect,
  onCheckIn,
  onCheckOut,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<"month" | "week" | "day">("month");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const handlePreviousPeriod = () => {
    if (viewType === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewType === "week") {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const handleNextPeriod = () => {
    if (viewType === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewType === "week") {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getAttendanceForDate = (date: Date) => {
    const key = format(date, "yyyy-MM-dd");
    return attendanceData[key];
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="grid grid-cols-7 gap-1 p-4">
        {/* Day headers */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center font-semibold text-sm text-muted-foreground p-2"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day) => {
          const attendance = getAttendanceForDate(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isTodayDate = isToday(day);
          const isWeekendDay = isWeekend(day);

          return (
            <div
              key={day.toISOString()}
              onClick={() => {
                setSelectedDate(day);
                onDateSelect?.(day);
              }}
              className={cn(
                "p-2 min-h-28 border rounded-md cursor-pointer transition-all flex flex-col",
                "hover:bg-accent hover:border-primary",
                !isCurrentMonth && "opacity-50 bg-muted/20",
                isSelected && "ring-2 ring-primary bg-primary/5",
                isTodayDate && "ring-2 ring-blue-500 border-blue-500",
                isWeekendDay && "bg-muted/30",
              )}
            >
              <div className="text-xs font-semibold mb-1">
                {format(day, "d")}
              </div>

              {attendance && (
                <div className="space-y-1 flex-1 overflow-hidden">
                  <div
                    className={cn(
                      "h-1.5 w-full rounded-full",
                      STATUS_CONFIG[attendance.status].color,
                    )}
                  />
                  <div className="text-[10px] text-foreground/70 space-y-0.5">
                    {attendance.checkInTime && (
                      <div className="font-medium flex items-center gap-1">
                        🟢 {attendance.checkInTime}
                        {attendance.is_auto_checked_in && (
                          <Lightning className="h-2 w-2 text-blue-500" weight="fill" aria-label="Auto check-in" />
                        )}
                      </div>
                    )}
                    {attendance.checkOutTime && attendance.is_auto_checked_out && (
                      <div className="font-medium flex items-center gap-1 text-orange-500">
                        <Lightning className="h-2 w-2" weight="fill" aria-label="Auto check-out" />
                        Auto
                      </div>
                    )}
                    {attendance.totalHours && (
                      <div className="font-semibold">
                        {attendance.totalHours}h
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="overflow-x-auto p-4">
        <div className="grid grid-cols-8 gap-1 min-w-max">
          {/* Time column header */}
          <div className="col-span-1 text-xs font-semibold p-2">Time</div>

          {/* Day headers */}
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "text-center font-semibold text-xs p-2 border rounded",
                isToday(day) && "bg-blue-500/10 border-blue-500",
              )}
            >
              <div>{format(day, "EEE")}</div>
              <div>{format(day, "d")}</div>
            </div>
          ))}

          {/* Time slots */}
          {hours.map((hour) => (
            <React.Fragment key={`hour-${hour}`}>
              {/* Time label */}
              <div className="text-xs text-muted-foreground p-2 text-right">
                {String(hour).padStart(2, "0")}:00
              </div>

              {/* Day cells */}
              {weekDays.map((day) => {
                const attendance = getAttendanceForDate(day);
                return (
                  <div
                    key={`${day.toISOString()}-${hour}`}
                    className={cn(
                      "p-1 border border-border min-h-8 cursor-pointer hover:bg-accent",
                      attendance &&
                        STATUS_CONFIG[attendance.status].color + "/20",
                    )}
                    onClick={() => {
                      setSelectedDate(day);
                      onDateSelect?.(day);
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const selected = selectedDate || currentDate;
    const attendance = getAttendanceForDate(selected);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const intervals = Array.from({ length: 24 * 4 }, (_, i) => i); // 15-min intervals

    return (
      <div className="p-4 space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold">
            {format(selected, "EEEE, MMMM d, yyyy")}
          </h3>
        </div>

        {attendance && (
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div
                className={cn(
                  "w-4 h-4 rounded-full",
                  STATUS_CONFIG[attendance.status].color,
                )}
              />
              <span className="font-semibold">
                {STATUS_CONFIG[attendance.status].label}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              {attendance.checkInTime && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Check-in:</span>
                  <span className="font-medium">{attendance.checkInTime}</span>
                  {attendance.is_auto_checked_in && (
                    <div className="flex items-center gap-1 text-blue-500 text-xs">
                      <Lightning className="h-3 w-3" weight="fill" />
                      <span>Auto</span>
                    </div>
                  )}
                </div>
              )}
              {attendance.checkOutTime && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Check-out:</span>
                  <span className="font-medium">{attendance.checkOutTime}</span>
                  {attendance.is_auto_checked_out && (
                    <div className="flex items-center gap-1 text-orange-500 text-xs">
                      <Lightning className="h-3 w-3" weight="fill" />
                      <span>Auto</span>
                    </div>
                  )}
                </div>
              )}
              {attendance.totalHours && (
                <div>
                  <span className="text-muted-foreground">Total Hours:</span>{" "}
                  <span className="font-medium">{attendance.totalHours}h</span>
                </div>
              )}
              {attendance.notes && (
                <div>
                  <span className="text-muted-foreground">Notes:</span>{" "}
                  <span>{attendance.notes}</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* 15-minute interval grid */}
        <div className="space-y-1">
          {Array.from({ length: 24 }).map((_, hourIdx) => (
            <div key={`hour-${hourIdx}`}>
              <div className="text-xs font-semibold text-muted-foreground mb-1">
                {String(hourIdx).padStart(2, "0")}:00
              </div>
              <div className="grid grid-cols-4 gap-1">
                {Array.from({ length: 4 }).map((_, minIdx) => (
                  <div
                    key={`interval-${hourIdx}-${minIdx}`}
                    className="h-4 border border-border rounded hover:bg-accent cursor-pointer"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const formatHeader = () => {
    if (viewType === "month") {
      return format(currentDate, "MMMM yyyy");
    } else if (viewType === "week") {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
    } else {
      return format(selectedDate || currentDate, "EEEE, MMMM d, yyyy");
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePreviousPeriod}>
            <CaretLeft size={20} />
          </Button>
          <span className="min-w-32 text-center font-semibold">
            {formatHeader()}
          </span>
          <Button variant="outline" size="icon" onClick={handleNextPeriod}>
            <CaretRight size={20} />
          </Button>
        </div>

        <Button variant="outline" onClick={handleToday}>
          Today
        </Button>
      </div>

      <Tabs value={viewType} onValueChange={(v) => setViewType(v as any)}>
        <TabsList className="grid w-full max-w-xs grid-cols-3">
          <TabsTrigger value="month">Month</TabsTrigger>
          <TabsTrigger value="week">Week</TabsTrigger>
          <TabsTrigger value="day">Day</TabsTrigger>
        </TabsList>

        <TabsContent value="month" className="border rounded-lg">
          {renderMonthView()}
        </TabsContent>

        <TabsContent value="week" className="border rounded-lg">
          {renderWeekView()}
        </TabsContent>

        <TabsContent value="day" className="border rounded-lg">
          {renderDayView()}
        </TabsContent>
      </Tabs>

      {/* Status legend */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Status Legend</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => (
            <div key={status} className="flex items-center gap-2">
              <div className={cn("w-3 h-3 rounded-full", config.color)} />
              <span className="text-sm text-muted-foreground">
                {config.label}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AttendanceCalendar;
